import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Mail, ExternalLink, Copy, Check, User, Building2, Phone, MapPin, Quote, Radio, Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { useCanonical } from "../lib/seoHelpers";
import { safeJsonLd } from "../lib/safeJsonLd";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * /press — Press kit + quotable stats page.
 *
 * The single-source URL journalists land on. Everything they need to
 * cite Social Dispute Co:
 *   - Company facts (structured for direct quoting)
 *   - Founder bio (Mark Becker)
 *   - Downloadable logos (SVG + PNG)
 *   - 5 pull-quote stats they can cite with a one-click copy
 *   - Media contact
 */

const COMPANY_FACTS = [
  { label: "Legal name", value: "8eight8 LLC (DBA Social Dispute Co)" },
  { label: "Founded", value: "2024" },
  { label: "Headquarters", value: "Honolulu, Hawaii, USA" },
  { label: "Founder", value: "Mark Becker" },
  { label: "Service", value: "Independent documentation preparation for social media account appeals" },
  { label: "Pricing", value: "Free Assessment $0 · Case Review $99 · Appeal Package $399 · Priority Support $499" },
  { label: "Platforms covered", value: "Instagram, Facebook, TikTok, YouTube, X, LinkedIn, Snapchat, Reddit, Google Business Profile, Meta Business Manager" },
  { label: "Not affiliated with", value: "Any social media platform. We do not claim insider access." },
  { label: "Does not guarantee", value: "Platform decisions. Outcomes are determined by each platform's internal review." },
];

const QUOTABLE_STATS_FALLBACK = [
  {
    id: "stat-1",
    number: "22",
    unit: "policy categories",
    context: "Facebook Community Standards define 22 distinct enforcement areas. Business Pages and personal accounts follow the same standards but escalate through different appeal paths.",
    citation: "Social Dispute Co, Feb 2026 Platform Enforcement Brief",
    source: "documentation",
  },
  {
    id: "stat-2",
    number: "14",
    unit: "Community Guideline categories",
    context: "Meta enforces 14 Community Guideline categories on Instagram. Successful appeals map notice language to the specific sub-policy code Meta's reviewers work from.",
    citation: "Social Dispute Co, Instagram Enforcement Reference 2026",
    source: "documentation",
  },
  {
    id: "stat-3",
    number: "90-day",
    unit: "rolling window",
    context: "TikTok updated its 'Multiple Community Guidelines Violations' enforcement window in Jan 2026 from 60 to 90 days. This is the same window that triggers permanent account termination on the third strike.",
    citation: "Social Dispute Co ToS Change Tracker",
    source: "documentation",
  },
  {
    id: "stat-4",
    number: "3",
    unit: "strikes = channel termination",
    context: "YouTube terminates channels on the third Community Guidelines strike within a 90-day window. Copyright strikes and Content ID claims follow a separate path.",
    citation: "Social Dispute Co, YouTube Appeal Reference",
    source: "documentation",
  },
  {
    id: "stat-5",
    number: "10",
    unit: "policy pillars (TikTok)",
    context: "TikTok Community Guidelines cluster around 10 primary policy pillars. Structured appeal documentation groups each strike, cites the pillar, and provides context per strike.",
    citation: "Social Dispute Co Platform Enforcement Reference",
    source: "documentation",
  },
];

export default function PressPage() {
  useCanonical("https://socialdisputeco.com/press");
  useEffect(() => {
    document.title = "Press Kit — Founder Bio, Logos, Quotable Stats | Social Dispute Co";
    const desc = "Press kit for Social Dispute Co: founder bio, downloadable logos, one-line company facts, and quotable enforcement stats for journalists covering social media platform enforcement.";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", desc);
  }, []);

  const [copiedId, setCopiedId] = useState("");
  const [stats, setStats] = useState(QUOTABLE_STATS_FALLBACK);
  const [statsMeta, setStatsMeta] = useState({ totalCases: 0, loading: true });

  // Fetch live-blended stats from the backend. Falls back gracefully to the
  // documentation-only seed set on any error.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/press/stats`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data.stats) && data.stats.length > 0) {
          setStats(data.stats);
        }
        setStatsMeta({
          totalCases: data.totalCases || 0,
          minVolumeThreshold: data.minVolumeThreshold || 50,
          lastUpdated: data.lastUpdated,
          loading: false,
        });
      } catch (_e) {
        if (!cancelled) setStatsMeta((m) => ({ ...m, loading: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCopy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 2000);
    } catch (_e) {
      // Silent
    }
  };

  const newsArticleSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "Social Dispute Co Press Kit",
    description: "Press kit including founder bio, company facts, downloadable logos, and quotable enforcement statistics.",
    publisher: { "@type": "Organization", name: "Social Dispute Co" },
    url: "https://socialdisputeco.com/press",
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]" data-testid="press-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(newsArticleSchema) }}
      />

      <Header />

      <main>
        {/* Hero */}
        <section className="pt-32 pb-12 bg-gradient-to-br from-[#3f4816]/40 to-[#1a1c1b]">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <p className="text-xs uppercase tracking-widest text-[#d9fb06] font-mono mb-3">
              Press kit · Free to cite · Interviews available
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-[#dfddd6] uppercase leading-[0.95] mb-5">
              Press &amp; Media Resources
            </h1>
            <p className="text-lg md:text-xl text-[#888680] max-w-2xl leading-relaxed">
              Everything a journalist, researcher, or podcast producer needs to cite Social Dispute Co accurately.
              Logos, one-line facts, quotable stats, and a direct line to the founder.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="mailto:press@socialdisputeco.com?subject=Interview%20request">
                <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 font-bold" data-testid="press-email-btn">
                  <Mail className="w-4 h-4 mr-2" />
                  press@socialdisputeco.com
                </Button>
              </a>
              <a href="#stats">
                <Button variant="outline" className="border-[#d9fb06] text-[#d9fb06] hover:bg-[#d9fb06]/10 font-bold">
                  Jump to Quotable Stats
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* One-line company facts */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-2 flex items-center gap-3">
              <Building2 className="w-6 h-6" />
              Company at a Glance
            </h2>
            <p className="text-sm text-[#888680] mb-8">
              Copy-paste-ready facts. Every field is intentionally single-line so it fits any citation format.
            </p>
            <dl className="space-y-3">
              {COMPANY_FACTS.map((fact) => (
                <div
                  key={fact.label}
                  className="rounded-xl border border-[#3f4816]/40 bg-[#252825] p-4 md:p-5 flex flex-col md:flex-row gap-2 md:gap-6 md:items-center"
                  data-testid={`fact-${fact.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <dt className="text-xs uppercase tracking-widest text-[#888680] font-mono md:w-56 md:flex-shrink-0">
                    {fact.label}
                  </dt>
                  <dd className="text-[#dfddd6] text-sm md:text-base flex-1">{fact.value}</dd>
                  <button
                    onClick={() => handleCopy(`fact-${fact.label}`, fact.value)}
                    className="text-[#888680] hover:text-[#d9fb06] p-1.5 rounded-md hover:bg-[#3f4816]/20 flex-shrink-0"
                    title="Copy value"
                    data-testid={`copy-fact-${fact.label.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {copiedId === `fact-${fact.label}` ? <Check className="w-4 h-4 text-[#d9fb06]" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* Quotable stats */}
        <section id="stats" className="py-16 bg-[#252825] border-y border-[#3f4816]/40">
          <div className="max-w-5xl mx-auto px-5 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-2 flex items-center gap-3">
              <Quote className="w-6 h-6" />
              Quotable Statistics
            </h2>
            <p className="text-sm text-[#888680] mb-2 max-w-2xl">
              One-click copies the pre-formatted citation. We do <strong>not</strong> publish appeal-success percentages —
              any figure claiming a specific &ldquo;recovery rate&rdquo; is either fabricated or unverifiable.
            </p>
            <div className="mb-8 flex items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#d9fb06]/15 text-[#d9fb06] font-mono uppercase tracking-widest" data-testid="stats-source-badge">
                <Radio className="w-3 h-3" />
                {statsMeta.loading ? "Loading…" : statsMeta.totalCases > 0 ? `Live · n = ${statsMeta.totalCases.toLocaleString()} cases` : "Documentation sources"}
              </span>
              {statsMeta.lastUpdated && (
                <span className="text-[#888680] font-mono">
                  Updated {new Date(statsMeta.lastUpdated).toLocaleDateString()}
                </span>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {stats.map((stat) => {
                const fullCitation = `"${stat.context}" — ${stat.citation}`;
                const isLive = stat.source === "live";
                return (
                  <article
                    key={stat.id}
                    className="rounded-2xl border border-[#3f4816]/50 bg-[#1a1c1b] p-6 relative"
                    data-testid={`stat-${stat.id}`}
                  >
                    {isLive && (
                      <span
                        className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#d9fb06] text-[#1a1c1b] text-[10px] font-black uppercase tracking-widest"
                        data-testid={`live-badge-${stat.id}`}
                        title="Sourced from live analyzer data"
                      >
                        Live
                      </span>
                    )}
                    <p className="text-4xl md:text-5xl font-black text-[#d9fb06] font-mono mb-1">{stat.number}</p>
                    <p className="text-xs uppercase tracking-widest text-[#888680] font-mono mb-4">
                      {stat.unit}
                    </p>
                    <p className="text-sm md:text-base text-[#dfddd6] leading-relaxed mb-4">{stat.context}</p>
                    <p className="text-xs text-[#888680] italic mb-4">Source: {stat.citation}</p>
                    <button
                      onClick={() => handleCopy(stat.id, fullCitation)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#302f2c] border border-[#3f4816] text-[#d9fb06] text-xs font-bold hover:bg-[#3f4816]/40 transition"
                      data-testid={`copy-${stat.id}`}
                    >
                      {copiedId === stat.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {copiedId === stat.id ? "Citation copied" : "Copy citation"}
                    </button>
                  </article>
                );
              })}
            </div>
            <p className="mt-8 text-sm text-[#888680]">
              Need original data or a custom pull for your story?{" "}
              <a href="mailto:press@socialdisputeco.com?subject=Data%20request" className="text-[#d9fb06] hover:underline">
                Email press@socialdisputeco.com
              </a>{" "}
              — we&apos;re happy to share methodology or run a specific breakdown.
            </p>
          </div>
        </section>

        {/* Founder bio */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-2 flex items-center gap-3">
              <User className="w-6 h-6" />
              Founder Bio
            </h2>
            <p className="text-sm text-[#888680] mb-8">Ready to quote. Long and short versions.</p>

            <div className="rounded-2xl border border-[#3f4816]/50 bg-[#252825] p-6 md:p-8 mb-6" data-testid="founder-bio-long">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs uppercase tracking-widest text-[#888680] font-mono">Long-form (75 words)</span>
                <button
                  onClick={() => handleCopy("bio-long", "Mark Becker is the founder of Social Dispute Co, an independent documentation-preparation service for people whose social media accounts have been suspended, disabled, or banned. Based in Honolulu, Hawaii and operated under 8eight8 LLC, the company prepares structured appeal documentation for Instagram, Facebook, TikTok, YouTube, X, LinkedIn, Snapchat, Reddit, and Google Business Profile cases. Becker founded Social Dispute Co in 2024 after seeing how frequently platform enforcement decisions leave users without a clear appeal path.")}
                  className="text-[#888680] hover:text-[#d9fb06] p-1"
                  title="Copy bio"
                  data-testid="copy-bio-long"
                >
                  {copiedId === "bio-long" ? <Check className="w-4 h-4 text-[#d9fb06]" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[#dfddd6] leading-relaxed">
                Mark Becker is the founder of Social Dispute Co, an independent documentation-preparation service
                for people whose social media accounts have been suspended, disabled, or banned. Based in Honolulu,
                Hawaii and operated under 8eight8 LLC, the company prepares structured appeal documentation for
                Instagram, Facebook, TikTok, YouTube, X, LinkedIn, Snapchat, Reddit, and Google Business Profile
                cases. Becker founded Social Dispute Co in 2024 after seeing how frequently platform enforcement
                decisions leave users without a clear appeal path.
              </p>
            </div>

            <div className="rounded-2xl border border-[#3f4816]/50 bg-[#252825] p-6 md:p-8" data-testid="founder-bio-short">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs uppercase tracking-widest text-[#888680] font-mono">Short-form (25 words)</span>
                <button
                  onClick={() => handleCopy("bio-short", "Mark Becker is the founder of Social Dispute Co, a Honolulu-based independent documentation-preparation service for people whose social media accounts have been suspended, disabled, or banned.")}
                  className="text-[#888680] hover:text-[#d9fb06] p-1"
                  title="Copy short bio"
                  data-testid="copy-bio-short"
                >
                  {copiedId === "bio-short" ? <Check className="w-4 h-4 text-[#d9fb06]" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[#dfddd6] leading-relaxed">
                Mark Becker is the founder of Social Dispute Co, a Honolulu-based independent documentation-preparation
                service for people whose social media accounts have been suspended, disabled, or banned.
              </p>
            </div>
          </div>
        </section>

        {/* Logos & assets */}
        <section className="py-16 bg-[#252825] border-y border-[#3f4816]/40">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-2 flex items-center gap-3">
              <Download className="w-6 h-6" />
              Logos &amp; Brand Assets
            </h2>
            <p className="text-sm text-[#888680] mb-8">
              Free to use in editorial coverage. Please don&apos;t alter proportions or recolor outside the palette.
            </p>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="rounded-xl border border-[#3f4816]/40 bg-[#1a1c1b] p-6 text-center">
                <div className="h-24 flex items-center justify-center mb-4">
                  <img src="/favicon.svg" alt="Social Dispute Co logo" className="h-16 w-16" />
                </div>
                <p className="text-sm text-[#dfddd6] font-semibold mb-1">Primary mark (SVG)</p>
                <p className="text-xs text-[#888680] mb-4">Vector · Scales to any size</p>
                <a
                  href="/favicon.svg"
                  download="social-dispute-co-logo.svg"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#d9fb06] text-[#1a1c1b] text-sm font-bold hover:bg-[#d9fb06]/90"
                  data-testid="download-logo-svg"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download SVG
                </a>
              </div>
              <div className="rounded-xl border border-[#3f4816]/40 bg-[#1a1c1b] p-6 text-center">
                <div className="h-24 flex items-center justify-center mb-4">
                  <img src="/og-image.png" alt="Social Dispute Co OG image" className="h-24 rounded-lg" />
                </div>
                <p className="text-sm text-[#dfddd6] font-semibold mb-1">Social preview (1200×630 PNG)</p>
                <p className="text-xs text-[#888680] mb-4">Editorial use · Includes brand mark</p>
                <a
                  href="/og-image.png"
                  download="social-dispute-co-social.png"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#d9fb06] text-[#1a1c1b] text-sm font-bold hover:bg-[#d9fb06]/90"
                  data-testid="download-logo-png"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download PNG
                </a>
              </div>
            </div>
            <div className="mt-6 rounded-xl border border-[#3f4816]/40 bg-[#1a1c1b] p-5">
              <p className="text-xs uppercase tracking-widest text-[#888680] font-mono mb-2">Brand palette</p>
              <div className="flex gap-3 flex-wrap">
                <ColorSwatch hex="#d9fb06" name="Signal Yellow" />
                <ColorSwatch hex="#1a1c1b" name="Base Black" />
                <ColorSwatch hex="#252825" name="Surface" />
                <ColorSwatch hex="#dfddd6" name="Text" />
                <ColorSwatch hex="#3f4816" name="Deep Olive" />
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-5 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-6">Direct Contact</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <ContactCard icon={Mail} label="Press email" value="press@socialdisputeco.com" href="mailto:press@socialdisputeco.com" />
              <ContactCard icon={Phone} label="Phone" value="808-301-2134" href="tel:+18083012134" />
              <ContactCard icon={MapPin} label="Location" value="Honolulu, Hawaii, USA" />
            </div>
            <p className="mt-8 text-sm text-[#888680]">
              Reporters and podcasters: Mark is generally available for a 20-minute conversation with 48-hour notice.
              Include your outlet, angle, and deadline in your first email — it helps us respond faster.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function ColorSwatch({ hex, name }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-8 h-8 rounded-md border border-[#3f4816]/50" style={{ backgroundColor: hex }} />
      <div className="text-xs">
        <p className="text-[#dfddd6] font-semibold">{name}</p>
        <p className="text-[#888680] font-mono">{hex}</p>
      </div>
    </div>
  );
}

function ContactCard({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="rounded-xl border border-[#3f4816]/40 bg-[#252825] p-4 hover:border-[#d9fb06]/40 transition">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[#d9fb06]" />
        <span className="text-xs uppercase tracking-widest text-[#888680] font-mono">{label}</span>
      </div>
      <p className="text-[#dfddd6] text-sm break-all">{value}</p>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : inner;
}
